import React, { useEffect, useState } from "react";
import EmojiObjectsOutlinedIcon from "@mui/icons-material/EmojiObjectsOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import BookmarkRemoveOutlinedIcon from "@mui/icons-material/BookmarkRemoveOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import { HelpTooltip } from "../../components/HelpTooltip";

import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { Footer } from "../../components/Footer";
import {
  getUserDoubtStats,
  getUserSavedQuestions,
  toggleSavedQuestion,
} from "../../services/moodle/estudio";

import { EstudioBanner } from "./EstudioBanner";
import "./index.css";

/* ── helpers ── */
const formatSavedDate = (timestamp) => {
  if (!timestamp) return "Fecha no disponible";
  return new Intl.DateTimeFormat("es-CL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(timestamp * 1000));
};

const getRateClass = (rate) => {
  if (rate >= 70) return "high";
  if (rate >= 50) return "medium";
  return "low";
};

const ADVICE_MAP = {
  advicepending: "Responde y finaliza tus preguntas marcadas para recibir consejos personalizados.",
  advicebelow50: "No te la juegues el día del examen.",
  advice50to64: "Si el día del examen has respondido las justas no te la juegues. Es un cara o cruz.",
  advice65plus: "¡Juégatela el día del examen! Te favorece.",
};

/* ── Component ── */
export const EstudioPage = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isDark = theme?.mode === "dark";

  const [stats, setStats] = useState(null);
  const [savedQuestions, setSavedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingIds, setRemovingIds] = useState([]);

  const token = user?.token || localStorage.getItem("token");

  useEffect(() => {
    let isCancelled = false;

    const loadData = async () => {
      if (!token) {
        setError("No se encontró una sesión activa para cargar tu información de estudio.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [statsData, questionsData] = await Promise.all([
          getUserDoubtStats({ token }),
          getUserSavedQuestions({ token }),
        ]);

        if (!isCancelled) {
          setStats(statsData);
          setSavedQuestions(Array.isArray(questionsData) ? questionsData : []);
          setError(null);
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err.message || "No se pudo cargar la información de estudio.");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isCancelled = true;
    };
  }, [token]);

  const handleRemoveSavedQuestion = async (question) => {
    const questionId = question.question_id;
    setRemovingIds((current) => [...current, questionId]);

    try {
      const result = await toggleSavedQuestion({
        token,
        questionId,
        quizId: question.quiz_id,
        cmid: question.cmid,
        saved: false,
      });

      if (!result.success || result.saved) {
        throw new Error(result.message || "No se pudo quitar la pregunta guardada.");
      }

      setSavedQuestions((current) =>
        current.filter((q) => q.question_id !== questionId)
      );
    } catch (err) {
      setError(err.message || "No se pudo quitar la pregunta guardada.");
    } finally {
      setRemovingIds((current) => current.filter((id) => id !== questionId));
    }
  };

  // Ring calculations
  const rawRate = Number(stats?.success_rate_on_doubts ?? 0);
  const successRate = Math.round(rawRate);
  const totalMarked = stats?.total_doubts_marked ?? 0;
  const totalCorrect = stats?.doubts_correct ?? 0;
  const totalIncorrect = stats?.doubts_incorrect ?? 0;

  const radius = 68;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (successRate / 100) * circumference;

  const skelBg = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";

  return (
    <>
      <EstudioBanner />

      <main style={{ minHeight: "calc(100vh - 106px)", paddingBottom: 56 }}>
        <div className="page-content-shell estudio-page">
          {loading ? (
            /* ── Loading Skeleton ── */
            <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
              <div className="es-skel-header">
                <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: skelBg }} />
                <Skeleton variant="text" width={320} height={36} sx={{ bgcolor: skelBg }} />
              </div>
              <Skeleton variant="rounded" height={190} sx={{ borderRadius: "8px", bgcolor: skelBg }} />

              <div className="es-skel-header">
                <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: skelBg }} />
                <Skeleton variant="text" width={280} height={36} sx={{ bgcolor: skelBg }} />
              </div>
              <Skeleton variant="rounded" height={160} sx={{ borderRadius: "8px", bgcolor: skelBg }} />

              <div className="es-skel-header">
                <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: skelBg }} />
                <Skeleton variant="text" width={300} height={36} sx={{ bgcolor: skelBg }} />
              </div>
              <Skeleton variant="rounded" height={200} sx={{ borderRadius: "8px", bgcolor: skelBg }} />
            </div>
          ) : error && !stats ? (
            /* ── Error Banner ── */
            <div className="es-error-banner">
              <h3 className="es-error-title">No se pudo cargar tu información</h3>
              <p className="es-error-desc">{error}</p>
            </div>
          ) : (
            /* ── Main Layout: 3 Sections ── */
            <div style={{ display: "flex", flexDirection: "column", gap: 48 }}>
              {error && (
                <div className="es-error-banner">
                  <p className="es-error-desc">{error}</p>
                </div>
              )}

              {/* 1 · HERO: Me la Juego */}
              <section>
                <div className="es-header">
                  <span className="es-header-icon" role="img" aria-label="me la juego">
                    <EmojiObjectsOutlinedIcon sx={{ fontSize: 32, color: "#ecb236" }} />
                  </span>
                  <h2>
                    <strong>Me la Juego</strong> <span className="es-sep">|</span>{" "}
                    <span className="es-header-subtitle">Tu criterio bajo duda</span>
                  </h2>
                </div>

                <article className="es-hero-card">
                  <div className="es-ring-container">
                    <svg className="es-ring-svg" viewBox="0 0 160 160" aria-hidden="true">
                      <circle className="es-ring-bg" cx="80" cy="80" r={radius} />
                      <circle
                        className="es-ring-progress"
                        cx="80"
                        cy="80"
                        r={radius}
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                      />
                    </svg>
                    <div className="es-ring-text">
                      <span className="es-ring-value">{successRate}%</span>
                      <span className="es-ring-label">de acierto</span>
                    </div>
                  </div>

                  <div className="es-hero-content">
                    <span className="es-hero-tag">
                      <EmojiObjectsOutlinedIcon sx={{ fontSize: 18 }} />
                      Diagnóstico de precisión
                    </span>
                    <h3 className="es-hero-advice es-title-with-help">
                      <span>{(stats?.advice_key && ADVICE_MAP[stats.advice_key]) ||
                        stats?.advice_message ||
                        "Aún no hay suficientes respuestas para generar una recomendación."}</span>
                      <HelpTooltip
                        text={'Esta métrica evalúa tus respuestas cuando decidiste activar "Me la Juego" en preguntas dudosas consolidadas.'}
                        ariaLabel="Más información sobre Diagnóstico de precisión"
                        className="es-title-help"
                      />
                    </h3>

                    <div className="es-hero-stats">
                      <span className="es-inline-stat">
                        <span className="es-dot correct" aria-hidden="true" />
                        <strong>{totalCorrect}</strong>&nbsp;acertadas
                      </span>
                      <span className="es-inline-stat">
                        <span className="es-dot incorrect" aria-hidden="true" />
                        <strong>{totalIncorrect}</strong>&nbsp;falladas
                      </span>
                      <span className="es-inline-stat">
                        <span className="es-dot marked" aria-hidden="true" />
                        <strong>{totalMarked}</strong>&nbsp;marcadas
                      </span>
                    </div>
                  </div>
                </article>
              </section>

              {/* 2 · POR CUESTIONARIO */}
              <section>
                <div className="es-header">
                  <span className="es-header-icon" role="img" aria-label="cuestionarios">
                    <QuizOutlinedIcon sx={{ fontSize: 32, color: "#ecb236" }} />
                  </span>
                  <h2>
                    <strong>Por Cuestionario</strong> <span className="es-sep">|</span>{" "}
                    <span className="es-header-subtitle">Rendimiento por evaluación</span>
                  </h2>
                </div>

                <div className="es-card">
                  {stats?.quizzes?.length ? (
                    <div className="es-quiz-list">
                      {stats.quizzes.map((quiz) => {
                        const correct = Number(quiz.correct) || 0;
                        const incorrect = Number(quiz.incorrect) || 0;
                        const resolved = correct + incorrect;
                        const rate = resolved ? Math.round((correct / resolved) * 100) : 0;

                        return (
                          <div key={quiz.quiz_id} className="es-quiz-row">
                            <div className="es-quiz-name-col">
                              <p className="es-quiz-title" title={quiz.quiz_name}>
                                {quiz.quiz_name}
                              </p>
                              <p className="es-quiz-attempts">
                                {quiz.attempts} {quiz.attempts === 1 ? "intento" : "intentos"} · {quiz.doubts_marked} dudas
                              </p>
                            </div>

                            <div className="es-quiz-bar-wrap">
                              <div className="es-quiz-bar-line">
                                <div className="es-quiz-bar-track">
                                  <div
                                    className="es-quiz-bar-fill"
                                    style={{ width: `${rate}%` }}
                                  />
                                </div>
                                <span className={`es-quiz-rate ${getRateClass(rate)}${resolved ? "" : " is-empty"}`}>
                                  {resolved ? `${rate}% acierto` : "Sin datos"}
                                </span>
                              </div>
                              <div className="es-quiz-bar-counts">
                                {correct} {correct === 1 ? "acertada" : "acertadas"} · {incorrect} {incorrect === 1 ? "fallada" : "falladas"}
                              </div>
                            </div>

                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="es-empty-state">
                      <div className="es-empty-icon">
                        <QuizOutlinedIcon sx={{ fontSize: 26 }} />
                      </div>
                      <h4 className="es-empty-title">Sin evaluaciones registradas</h4>
                      <p className="es-empty-desc">
                        A medida que completes cuestionarios marcando tus dudas con "Me la Juego", verás aquí tu desglose comparativo.
                      </p>
                    </div>
                  )}
                </div>
              </section>

              {/* 3 · MIS PREGUNTAS GUARDADAS */}
              <section>
                <div className="es-header">
                  <span className="es-header-icon" role="img" aria-label="preguntas guardadas">
                    <BookmarkBorderOutlinedIcon sx={{ fontSize: 32, color: "#ecb236" }} />
                  </span>
                  <h2>
                    <strong>Mis Preguntas Guardadas</strong> <span className="es-sep">|</span>{" "}
                    <span className="es-header-subtitle">Tu banco de repaso</span>
                  </h2>
                </div>

                <div className="es-table-card">
                  {savedQuestions.length ? (
                    <>
                      <div className="es-desktop-table es-table-wrapper">
                        <table className="es-table">
                          <thead>
                            <tr>
                              <th style={{ width: "45%" }}>Pregunta</th>
                              <th style={{ width: "25%" }}>Origen</th>
                              <th style={{ width: "15%" }}>Fecha</th>
                              <th style={{ width: "15%", textAlign: "right" }}>Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {savedQuestions.map((q) => {
                              const isRemoving = removingIds.includes(q.question_id);
                              return (
                                <tr key={q.question_id}>
                                  <td className="es-td-question">
                                    {q.question_name || `Pregunta ${q.question_id}`}
                                  </td>
                                  <td>
                                    <div className="es-td-origin">
                                      {q.course_name && (
                                        <span className="es-course-tag" title={q.course_name}>
                                          <SchoolOutlinedIcon sx={{ fontSize: 15 }} />
                                          {q.course_name}
                                        </span>
                                      )}
                                      {q.quiz_name && (
                                        <span className="es-quiz-source">{q.quiz_name}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="es-td-date">
                                    {formatSavedDate(q.time_saved)}
                                  </td>
                                  <td style={{ textAlign: "right" }}>
                                    <button
                                      type="button"
                                      disabled={isRemoving}
                                      onClick={() => handleRemoveSavedQuestion(q)}
                                      className="es-btn-remove"
                                      aria-label={`Quitar ${q.question_name || q.question_id}`}
                                    >
                                      {isRemoving ? (
                                        <>
                                          <CircularProgress size={12} sx={{ color: "currentColor" }} />
                                          <span>Quitando...</span>
                                        </>
                                      ) : (
                                        <>
                                          <BookmarkRemoveOutlinedIcon sx={{ fontSize: 16 }} />
                                          <span>Quitar</span>
                                        </>
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="es-mobile-cards">
                        {savedQuestions.map((q) => {
                          const isRemoving = removingIds.includes(q.question_id);
                          return (
                            <div key={`mob-q-${q.question_id}`} className="es-question-card">
                              <div className="flex items-center justify-between gap-2 mb-2">
                                {q.course_name ? (
                                  <span className="es-course-tag" title={q.course_name}>
                                    <SchoolOutlinedIcon sx={{ fontSize: 14 }} />
                                    <span className="truncate max-w-[190px]">{q.course_name}</span>
                                  </span>
                                ) : <span />}
                                <span className="es-td-date text-xs text-white/50">{formatSavedDate(q.time_saved)}</span>
                              </div>
                              <div className="mb-3">
                                <p className="es-td-question font-semibold text-white text-sm mb-1 leading-snug">
                                  {q.question_name || `Pregunta ${q.question_id}`}
                                </p>
                                {q.quiz_name && (
                                  <span className="es-quiz-source text-xs text-white/60">{q.quiz_name}</span>
                                )}
                              </div>
                              <div className="flex justify-end pt-2 border-t border-white/10">
                                <button
                                  type="button"
                                  disabled={isRemoving}
                                  onClick={() => handleRemoveSavedQuestion(q)}
                                  className="es-btn-remove"
                                  aria-label={`Quitar ${q.question_name || q.question_id}`}
                                >
                                  {isRemoving ? (
                                    <>
                                      <CircularProgress size={12} sx={{ color: "currentColor" }} />
                                      <span>Quitando...</span>
                                    </>
                                  ) : (
                                    <>
                                      <BookmarkRemoveOutlinedIcon sx={{ fontSize: 16 }} />
                                      <span>Quitar</span>
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="es-empty-state">
                      <div className="es-empty-icon">
                        <BookmarkBorderOutlinedIcon sx={{ fontSize: 26 }} />
                      </div>
                      <h4 className="es-empty-title">Aún no tienes preguntas guardadas</h4>
                      <p className="es-empty-desc">
                        Guarda preguntas durante tus cuestionarios para repasarlas cuando quieras antes de tus exámenes.
                      </p>
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
};

export default EstudioPage;
